import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { CheckCircle } from 'lucide-react';
import { API_BASE } from '../api';

export default function VendorRegister() {
  const { i18n } = useTranslation(['vendor', 'common']);
  const [form, setForm] = useState({
    company_name: '',
    contact_name: '',
    contact_email: '',
    contact_phone: '',
    website_url: '',
    product_categories: '',
    description: '',
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [submitted, setSubmitted] = useState(false);

  const toggleLang = () => {
    i18n.changeLanguage(i18n.language === 'zh' ? 'en' : 'zh');
  };

  const handleChange = (field: string) => (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setForm(prev => ({ ...prev, [field]: e.target.value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    if (!form.company_name || !form.contact_name || !form.contact_email) {
      setError('Company name, contact name, and email are required.');
      return;
    }

    setLoading(true);
    try {
      const res = await fetch(`${API_BASE}/vendor/register`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ...form, preferred_locale: i18n.language }),
      });
      const json = await res.json();
      if (res.ok) {
        setSubmitted(true);
      } else {
        setError(json.error || 'Submission failed');
      }
    } catch {
      setError('Something went wrong. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  if (submitted) {
    return (
      <div className="min-h-screen flex items-center justify-center px-4" style={{ backgroundColor: '#0B0B0B' }}>
        <div className="w-full max-w-[460px] text-center animate-fade-in">
          <div className="flex justify-center mb-6">
            <div className="w-16 h-16 flex items-center justify-center" style={{ backgroundColor: 'rgba(74,222,128,0.10)', borderRadius: '4px' }}>
              <CheckCircle className="w-8 h-8 text-green-400" />
            </div>
          </div>
          <h1 className="text-2xl font-display font-bold text-white mb-2">Application Submitted</h1>
          <p className="text-sm text-white/50 mb-6">We'll review your application and get back to you shortly.</p>
          <Link to="/" className="text-sm text-white/40 hover:text-white/70 transition-colors">Back to home</Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center px-4 py-12" style={{ backgroundColor: '#0B0B0B' }}>
      <div className="w-full max-w-[500px] animate-fade-in">
        <div className="mb-8">
          <div className="flex items-center justify-between mb-6">
            <Link to="/" className="inline-flex items-baseline gap-0">
              <span className="font-display font-bold text-xl text-white" style={{ letterSpacing: '-0.02em' }}>CreatorHub</span>
              <span className="font-display font-bold text-xl" style={{ color: '#FFD400', letterSpacing: '-0.02em' }}>360</span>
            </Link>
            <button onClick={toggleLang} className="text-xs px-2.5 py-1 transition-colors"
              style={{ color: 'rgba(255,255,255,0.5)', backgroundColor: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.08)', borderRadius: '2px' }}>
              {i18n.language === 'zh' ? 'English' : '中文'}
            </button>
          </div>
          <h1 className="text-2xl font-display font-bold text-white mb-1" style={{ letterSpacing: '-0.02em' }}>Become a Vendor</h1>
          <p className="text-sm text-white/45">Tell us about your business and products</p>
        </div>

        <form onSubmit={handleSubmit} className="p-7 space-y-5" style={{ backgroundColor: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.07)', borderRadius: '4px' }}>
          {error && (
            <div className="p-3 text-sm text-red-400" style={{ backgroundColor: 'rgba(239,68,68,0.08)', border: '1px solid rgba(239,68,68,0.18)', borderRadius: '2px' }}>{error}</div>
          )}

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-1.5">
              <label className="block text-xs font-semibold text-white/50 uppercase tracking-wider">Company Name *</label>
              <input value={form.company_name} onChange={handleChange('company_name')} className="input" required />
            </div>
            <div className="space-y-1.5">
              <label className="block text-xs font-semibold text-white/50 uppercase tracking-wider">Contact Name *</label>
              <input value={form.contact_name} onChange={handleChange('contact_name')} className="input" required />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-1.5">
              <label className="block text-xs font-semibold text-white/50 uppercase tracking-wider">Email *</label>
              <input type="email" value={form.contact_email} onChange={handleChange('contact_email')} className="input" required />
            </div>
            <div className="space-y-1.5">
              <label className="block text-xs font-semibold text-white/50 uppercase tracking-wider">Phone</label>
              <input value={form.contact_phone} onChange={handleChange('contact_phone')} className="input" />
            </div>
          </div>

          <div className="space-y-1.5">
            <label className="block text-xs font-semibold text-white/50 uppercase tracking-wider">Website</label>
            <input value={form.website_url} onChange={handleChange('website_url')} className="input" placeholder="https://" />
          </div>

          <div className="space-y-1.5">
            <label className="block text-xs font-semibold text-white/50 uppercase tracking-wider">Product Categories</label>
            <input value={form.product_categories} onChange={handleChange('product_categories')} className="input" placeholder="e.g., Apparel, Electronics, Home" />
          </div>

          <div className="space-y-1.5">
            <label className="block text-xs font-semibold text-white/50 uppercase tracking-wider">Tell us about your business</label>
            <textarea
              value={form.description}
              onChange={handleChange('description')}
              className="input min-h-[80px] resize-y"
              rows={3}
            />
          </div>

          <button type="submit" disabled={loading} className="btn-primary w-full">
            {loading ? 'Submitting...' : 'Submit Application'}
          </button>
        </form>

        <div className="mt-6 text-center">
          <p className="text-xs text-white/30">
            Already have an account?{' '}
            <Link to="/vendor/login" className="text-white/60 hover:text-white transition-colors">Sign in</Link>
          </p>
        </div>
      </div>
    </div>
  );
}
